import {
  WSActions,
  WSButton,
  WSContainer,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSForm,
  WSGrid,
  WSInfoBox,
  WSInputText,
  WSInputTextarea,
  WSLayout,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  BatchCreate,
  BatchProcessingStrategy,
  PayableImportBatchCreate,
  PayableImportBatchCreateType
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import * as Yup from "yup";

import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { analyticsService } from "@services/analyticsService";
import { queriesBatches } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { IdempotencyKey } from "../../../../v3/utils/idempotencyKey";

import {
  routeHelpersImportPayables,
  routeHelpersImportPayablesBatch,
  useGoToContractorImportStart
} from "../../paths";

import { useModalPayablesColumnsGuide } from "../../components/ModalPayableColumnsGuide";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { PayableColumnsStackedView } from "../../components/PayableColumnsStackedView";
import { DownloadPayableExampleTemplateButton } from "../../components/DownloadPayableExampleTemplateButton";

import styles from "./styles.module.scss";

type FormData = {
  name: string;
  notes: string;
};

export const RouteImportPayablesStart: React.FC = () => {
  useBrowserPageTitle("Bulk upload payables: Start");
  const payablesColumnsGuideModal = useModalPayablesColumnsGuide();
  const [createBatch, createBatchMeta] =
    queriesBatches.createBatch.useMutation();

  const goToPayableBatchDetails = routeHelpersImportPayablesBatch.useGoTo();
  const goToContractorImportStart = useGoToContractorImportStart();
  const goToPayablesBatches = routeHelpersImportPayables.useGoTo();
  const onBack = () => {
    goToPayablesBatches();
  };

  const onCreateUploadBatch = async (formData: FormData) => {
    const name = formData.name.trim();
    const notes = formData.notes.trim();
    const idempotencyKey = new IdempotencyKey({
      type: PayableImportBatchCreateType.PAYABLE_IMPORT,
      name,
      notes
    });

    // The SDK generates `BatchCreate` as `{ type: 'PayableImport' } &
    // PayableImportBatchCreate | …`, and intersecting the bare literal with the
    // nominal `PayableImportBatchCreateType` member collapses every property to
    // `never` — so no value satisfies it. Build the real variant type and cast
    // once here, at the SDK boundary.
    const batchCreate: PayableImportBatchCreate = {
      type: PayableImportBatchCreateType.PAYABLE_IMPORT,
      _configuration: {
        processingStrategy: BatchProcessingStrategy.MERGE
      },
      metadata: {
        ...(name ? { name } : {}),
        ...(notes ? { notes } : {})
      }
    };

    await createBatch(
      {
        idempotencyKey: idempotencyKey.value,
        batchCreate: batchCreate as BatchCreate
      },
      {
        onSuccess: batch => {
          idempotencyKey.destroy();
          analyticsService.track("Payable Bulk Upload Started");
          goToPayableBatchDetails(
            { batchId: batch.id as string },
            { state: { showBatchCreatedModal: true } }
          );
        }
      }
    );
  };

  return (
    <WSLayout
      headerCenter={
        <WSText.ParagraphSm weight="medium">
          Bulk upload payables
        </WSText.ParagraphSm>
      }
      onClose={onBack}
    >
      <WSDivider />
      <WSElement colorBackground="gray50">
        <WSContainer verticalPadding>
          <WSGrid gutter="L">
            <WSGrid.Item span={{ xs: "12", m: "7" }}>
              <WSText.Paragraph weight="medium" color="gray700">
                Bulk upload payables
              </WSText.Paragraph>

              <WSText.ParagraphSm weight="book" color="gray500">
                Use this importer tool to upload payables in a single batch with
                a spreadsheet. Here’s what to expect:
              </WSText.ParagraphSm>
              <WSPanel my="2XL" p="XL">
                <WSInfoBox className={styles.content}>
                  <ol>
                    <WSList>
                      <li>
                        <b>Set up your spreadsheet</b>
                        <ul>
                          <li>
                            Get started by formatting your spreadsheet. Please
                            review the reminders to the right.
                          </li>
                          <li>
                            A payable should be associated with existing
                            contractors. If you are looking to upload
                            contractors,{" "}
                            <WSButton.Link
                              onClick={() => goToContractorImportStart()}
                            >
                              click here.
                            </WSButton.Link>
                          </li>
                        </ul>
                      </li>

                      <li>
                        <b>Create batch and upload spreadsheet</b>
                        <ul>
                          <li>
                            When you’re ready, create an upload batch and upload
                            the formatted CSV or Excel spreadsheet of your
                            payables.
                          </li>
                          <li>
                            Should any information or formatting issues arise,
                            this is an opportunity to correct them. You have the
                            option to delete the batch and start again, if
                            needed.
                          </li>
                        </ul>
                      </li>

                      <li>
                        <b>Complete import</b>
                        <ul>
                          <li>
                            Once the spreadsheet is uploaded you can complete
                            the import and process the spreadsheet.
                          </li>
                          <li>
                            Import details includes information about import
                            statistics and outcomes (success or errors). If
                            applicable, any import errors can be corrected and
                            addressed with a subsequent import.
                          </li>
                        </ul>
                      </li>
                    </WSList>
                  </ol>
                </WSInfoBox>
              </WSPanel>

              <WSForm<FormData>
                onSubmit={onCreateUploadBatch}
                defaultValues={{ name: "", notes: "" }}
                validationSchema={Yup.object().shape({
                  name: Yup.string().max(120, "120 characters at most"),
                  notes: Yup.string().max(500, "500 characters at most")
                })}
              >
                <WSPanel mb="2XL" p="XL">
                  <WSList gap="L">
                    <WSText.ParagraphSm weight="book" color="gray500">
                      Name this batch so you can find it later. Both fields are
                      set when the batch is created and cannot be changed
                      afterwards.
                    </WSText.ParagraphSm>
                    <WSForm.Field
                      name="name"
                      label="Batch name (optional)"
                      component={WSInputText}
                      componentProps={{
                        placeholder: "e.g. March contractor payouts"
                      }}
                    />
                    <WSForm.Field
                      name="notes"
                      label="Notes (optional)"
                      component={WSInputTextarea}
                    />
                  </WSList>
                </WSPanel>

                <ErrorAlert
                  context="BulkBatchCreate"
                  error={createBatchMeta.error}
                  mb="XL"
                />

                <WSActions
                  alignment="fill"
                  buttons={[
                    {
                      name: "back",
                      kind: "Secondary",
                      type: "button",
                      label: "Cancel",
                      onClick: onBack
                    },
                    {
                      name: "next",
                      label: "Create upload batch",
                      loading: createBatchMeta.isLoading
                    }
                  ]}
                />
              </WSForm>
            </WSGrid.Item>

            <WSGrid.Item span={{ xs: "12", m: "5" }}>
              <WSPanel p="XL">
                <WSText.Paragraph weight="medium" color="gray600" mb="M">
                  Reminders
                </WSText.Paragraph>
                <WSList gap="L">
                  <WSInfoBox>
                    <b>Upload a spreadsheet of line items:</b>
                    <ul>
                      <li>
                        Upload a CSV or Excel spreadsheet of your line items.
                        Don't worry if your spreadsheet has other details, but
                        please make sure it contains the required columns to
                        associate payables with the intended contractors.
                      </li>
                      <li>
                        Optional fields capture more information when adding
                        payables.
                      </li>
                    </ul>

                    <PayableColumnsStackedView my="L" mx="XL" />

                    <WSText.ParagraphSm weight="book" color="gray600" inline>
                      Need help setting up your spreadsheet?
                      <WSButton.Link
                        onClick={() => {
                          payablesColumnsGuideModal.open();
                        }}
                      >
                        Learn more here.
                      </WSButton.Link>
                    </WSText.ParagraphSm>

                    <DownloadPayableExampleTemplateButton mt="L" />
                  </WSInfoBox>
                  <WSInfoBox px="L" py="XL">
                    <b>Tax filing (1099s):</b>
                    <WSText.ParagraphSm weight="book" color="gray600" mb="XL">
                      If you are filing 1099s with Wingspan, this information is
                      relevant to you.
                    </WSText.ParagraphSm>
                    <ul>
                      <li>
                        Contractors receiving payments on Wingspan will
                        automatically populate their 1099-NECs (U.S. only) when
                        it comes time to generate tax forms.
                      </li>
                      <li>
                        If you've been making payments outside of Wingspan or
                        switched to Wingspan mid-year, ensure that you’ve
                        uploaded all contractors (through the contractors page)
                        and their payments information using this importer by
                        each January filing deadline to complete any gaps in
                        payment information.
                      </li>
                    </ul>

                    <WSText.ParagraphSm weight="book" color="gray600" inline>
                      Missing out on tax filing with Wingspan?
                      <WSButton.Link
                        onClick={() => {
                          openInNewTab(
                            "https://www.wingspan.app/solutions/offload-1099-filing"
                          );
                        }}
                      >
                        Learn more here.
                      </WSButton.Link>
                    </WSText.ParagraphSm>
                  </WSInfoBox>
                </WSList>
                <WSFlexBox.CenterY mb="M">
                  <WSText.ParagraphSm singleLine color="gray500">
                    Looking for past imports?
                  </WSText.ParagraphSm>
                  <WSButton.Link
                    icon="details"
                    rightIcon="chevron-right"
                    size="M"
                    name="bulkUploadPayables"
                    onClick={() => goToPayablesBatches()}
                  >
                    See imports
                  </WSButton.Link>
                </WSFlexBox.CenterY>

                <WSFlexBox.CenterY>
                  <WSText.ParagraphSm singleLine color="gray500">
                    Looking to bulk upload contractors?
                  </WSText.ParagraphSm>
                  <WSButton.Link
                    icon="upload"
                    rightIcon="chevron-right"
                    size="M"
                    name="bulkUploadContractors"
                    onClick={() => goToContractorImportStart()}
                  >
                    Bulk upload contractors
                  </WSButton.Link>
                </WSFlexBox.CenterY>
              </WSPanel>
            </WSGrid.Item>
          </WSGrid>
        </WSContainer>
      </WSElement>
    </WSLayout>
  );
};
